import { $manualAudit, $manualAuditCreate, ManualEvaluationSource } from '../constants/manual-audit';
import { IUploads } from './uploads.interface';
import { errorMessagesNames } from '../constants/errors';
import { accountStatus } from '../constants/user';
import { IDigitalProperty } from './digital-property.interface';
import { IUser, IUserSmallItem } from './user.interface';
import {
  $auditFinding,
  AuditFindingAccessibilityImpact,
  AuditFindingDisabilityAffected,
  AuditFindingFunctionalImpact,
  AuditFindingIssueFrequency,
  AuditFindingQAStatus,
  AuditFindingType,
} from '../constants/audit-finding';
import { $comment, $manualAuditIssueComment, ManualFindingFlagReason } from '../constants/comment';
import { ManualEvaluationAuditType } from '../constants/manual-audit-issues';
import { IWorkspace } from './workspace.interface';
import { ITaskClient } from './task.interface';
import { $task } from '../constants/task';
import { IAuditFinding, IAuditFindingViewport } from './audit-finding.interface';
import { IPopulatedManualFindingClient } from './manual-audit-finding.interface';
import {
  standardNameAndLevel,
  manualAuditStatus,
  manualAuditUserSettableStatus,
  scanAuthenticationType,
} from '../constants/scanning';
import { $pageWithComponents, $scan, $scanScope } from '../constants/scan';
import { IAiTestingPageResult, IScanScope, Origin } from './scan.interface';
import { ISecureWebAuthentication } from './scan-secuire-web-authentication.interface';
import { EvaluationMediums, EvaluationStandards } from '../constants/manual-evaluation';
import { ApiQueryOption } from '../constants/api';
import { Nullable } from './nullable.interface';
import { IParsedStandard } from './standard-parsing.interface';
import { ScopingCrawlStatus } from '../constants/scoping-crawl';

export interface IManualAudit {
  [$manualAudit._id]?: string;
  [$manualAudit.title]: string;
  [$manualAudit.digitalProperty]: string;
  [$manualAudit.createdBy]: string;
  [$manualAudit.createdAt]: Date;
  [$manualAudit.updatedAt]: Date;
  [$manualAudit.file]?: string;
  [$scan.conformanceLevel]?: standardNameAndLevel;
  [$manualAudit.status]?: manualAuditStatus;
  [$manualAudit.scope]?: IScanScope;
  [$manualAudit.source]?: ManualEvaluationSource;
}

export interface IPopulatedManualAudit {
  [$manualAudit._id]?: string;
  [$manualAudit.title]: string;
  [$manualAudit.digitalProperty]: IDigitalProperty;
  [$manualAudit.createdBy]: IUser;
  [$manualAudit.createdAt]: Date;
  [$manualAudit.updatedAt]: Date;
  [$manualAudit.file]?: IUploads[];
  [$manualAudit.issuesCount]: number;
  [$manualAudit.isArchived]?: boolean;
  [$manualAudit.workspace]?: IWorkspace;
  [$manualAudit.archivedBy]?: IUser;
  [$scan.conformanceLevel]?: standardNameAndLevel;
  [$manualAudit.status]?: manualAuditStatus;
  [$manualAudit.scope]?: IScanScope;
  [$manualAudit.source]?: ManualEvaluationSource;
  [$manualAudit.origin]?: Origin;
  [$manualAudit.isGoldenEvaluation]?: boolean;
  aiTestingPageResults?: IAiTestingPageResult[];
  latestScopingCrawl?: {
    status: ScopingCrawlStatus;
    updatedAt: Date;
  };
}

export interface IManualAuditServerResponse {
  manualAudits: IPopulatedManualAudit[];
  _total: number;
}

interface IManualAuditIssueServerResponseBase {
  manualAuditTitle: string;
  manualAuditStandards: EvaluationStandards[];
  issueUrl: string;
  linkedTask: ITaskClient;
}

export type IManualAuditIssueServerResponse = IManualAuditIssueServerResponseBase & {
  issue: IAuditFinding;
  oldStatus: AuditFindingQAStatus;
  taskStatusUpdated: boolean;
};

export type IPopulatedManualAuditIssueServerResponse = IManualAuditIssueServerResponseBase & {
  issue: IPopulatedManualFindingClient;
};

export interface IAuditCreatorError {
  type?: errorMessagesNames;
  expected?: string;
  actual?: any;
  timestamp: string;
}

export type ManualIssueCountByStatus = Partial<Record<AuditFindingQAStatus, number>>;
export interface ManualIssueProgress {
  fixed: number;
  remaining: number;
  total: number;
  progress: number;
}

export interface ManualFindingsProgress {
  resolved: number;
  unresolved: number;
  total: number;
  progress: number;
}

export interface IAuditExcelCreatorError extends IAuditCreatorError {
  row?: number;
  col?: string;
}

export interface IManualEvaluation {
  _id: string;
  title: string;
  issueCounts: ManualIssueCountByStatus;
  issueProgress: ManualIssueProgress;
  resolvedProgress?: ManualFindingsProgress;
  lastUploadDate?: Date;
  lastUploadBy?: string;
  lastUploadByUserStatus?: accountStatus;
  conformanceLevel?: standardNameAndLevel;
  status: manualAuditStatus;
  isArchived: boolean;
  [$manualAudit.origin]: Origin;
  [$manualAudit.updatedAt]?: string;
  [$manualAudit.createdAt]?: string;
  [$manualAudit.scope]?: IScanScope;
  [$manualAudit.conformanceScores]?: IConformanceScore[];
  [$manualAudit.source]: ManualEvaluationSource | undefined;
  [$manualAudit.isGoldenEvaluation]?: boolean;
  aiTestingPageResults?: IAiTestingPageResult[];
}

export interface IConformanceScore {
  standard: EvaluationStandards;
  score: number;
}

export interface IGetManualEvaluationsResponse {
  evaluations: IManualEvaluation[];
  _total: number;
}

export interface IManualEvaluationQuery {
  searchTerm: string;
}

export interface IManualEvaluationAuditListItem {
  [$comment._id]?: string;
  [$comment.tenantId]?: string;
  [$comment.type]: ManualEvaluationAuditType;
  [$comment.user]: IUserSmallItem;
  [$comment.createdAt]: string;
  [$comment.updatedAt]?: string;
  [$comment.edited]?: boolean;

  // location fields
  [$manualAuditIssueComment.digitalProperty]?: string;
  [$manualAuditIssueComment.manualAuditFindingId]?: string;

  // common optional fields
  [$manualAuditIssueComment.message]?: string;

  // manual audit issue fields
  [$manualAuditIssueComment.screenshot]?: string;
  [$manualAuditIssueComment.recommendation]?: string;
  [$manualAuditIssueComment.manualAuditIssueId]?: string;
  [$manualAuditIssueComment.internal]?: boolean;

  // manual audit issue status fields
  [$manualAuditIssueComment.newManualAuditIssueStatus]?: AuditFindingQAStatus;

  // flag fields
  [$manualAuditIssueComment.flagReason]?: ManualFindingFlagReason;

  taskId?: string;
  [$task.taskProjectId]?: string;
  [$task.projectId]?: string;
}

export interface IPublicManualFindingComment {
  [$comment._id]?: string;
  [$comment.type]: ManualEvaluationAuditType;
  [$comment.user]: IUserSmallItem;
  [$comment.createdAt]: string;
  [$comment.updatedAt]?: string;
  [$manualAuditIssueComment.message]?: string;
  [$manualAuditIssueComment.flagReason]?: ManualFindingFlagReason;
  [$manualAuditIssueComment.internal]: boolean;
}

export interface IManualAuditIssuePublicCommentRequestBody {
  [$manualAuditIssueComment.message]: string;
  [$manualAuditIssueComment.internal]: boolean;
  [$manualAuditIssueComment.flagReason]?: ManualFindingFlagReason;
}

export interface IManualAuditIssuePublicCommentParams {
  digitalPropertyId: string;
  scanId: string;
  findingId: string;
}

export interface IManualAuditIssuePublicFlagCommentQuery {
  [ApiQueryOption.addFlag]?: boolean;
}

export interface IManualAuditFindingWithDigitalPropertyAndTaskProjectId {
  [$auditFinding._id]: string;
  [$auditFinding.issueId]: string;
  [$manualAudit.digitalProperty]: string;
  [$task.taskProjectId]: string;
}

export interface CustomRuleScope {
  [$scanScope.medium]: EvaluationMediums;
  [$scanScope.standards]: EvaluationStandards[];
  [$scanScope.pages]: ICreateManualEvalScopePage[];
}

export type ICreateManualEvalScope = CustomRuleScope;

export interface ICreateManualEvalScopePage {
  [$pageWithComponents.pageId]: string;
  /**
   * Not defined value will be inferred as empty array
   */
  [$pageWithComponents.componentIds]?: string[];
  [$pageWithComponents.applySwa]?: boolean;
}

export interface IManualEvalCreateRequest {
  [$manualAuditCreate.title]: string;
  [$manualAuditCreate.scope]?: ICreateManualEvalScope;
  [$scan.secureWebAuthentication]?: Nullable<ISecureWebAuthentication>;
  [$scan.authType]?: Nullable<scanAuthenticationType>;
  [$scan.authUser]?: Nullable<string>;
  [$scan.authPassword]?: Nullable<string>;
  [$scan.handlePopupsSelector]?: Nullable<string>;
  /** In the `recommendation` mode saving never completes scoping. */
  scopingMode?: ScopingMode;
  recommendedPages?: IRecommendedPagePosition[];
}

export type ScopingMode = 'recommendation' | 'manual';

export interface IRecommendedPagePosition {
  url: string;
  /** Index of its row in the scope table. */
  position: number;
}

export enum $copyAuditMode {
  reference = 'reference',
  blueprint = 'blueprint',
  ignore = 'ignore',
}

export interface ICopyPageDestinationReference {
  type: $copyAuditMode.reference;
  pageId: string;
}

export interface ICopyPageDestinationBlueprint {
  type: $copyAuditMode.blueprint;
  pageTitle: string;
}

export interface ICopyComponentDestinationReference {
  type: $copyAuditMode.reference;
  componentId: string;
}

export interface ICopyComponentDestinationBlueprint {
  type: $copyAuditMode.blueprint;
  name: string;
}

export interface ICopyComponentMapping {
  sourceComponentId: string;
  destination: ICopyComponentDestinationReference | ICopyComponentDestinationBlueprint;
}

export interface ICopyPageMapping {
  sourcePageId: string;
  destination: ICopyPageDestinationReference | ICopyPageDestinationBlueprint;
}

export interface IManualEvalCopyRequest {
  [$manualAudit.title]: string;
  [$manualAuditCreate.scope]: ICreateManualEvalScope;
  destinationWorkspaceId: string;
  destinationDigitalPropertyId: string;
  componentMapping: ICopyComponentMapping[];
  pageMapping: ICopyPageMapping[];
  [$scan.authType]?: Nullable<scanAuthenticationType>;
  [$scan.authUser]?: Nullable<string>;
  [$scan.authPassword]?: Nullable<string>;
  [$scan.secureWebAuthentication]?: Nullable<ISecureWebAuthentication>;
  [$scan.handlePopupsSelector]?: Nullable<string>;
}

export type IManualEvalEditRequest = IManualEvalCreateRequest;

export interface IManualAuditUpdateRequest {
  [$manualAudit.title]?: string;
  [$manualAudit.status]?: manualAuditUserSettableStatus;
  [$manualAudit.scope]?: ICreateManualEvalScope;
}

export interface ISetGoldenEvaluationRequest {
  [$manualAudit.isGoldenEvaluation]: boolean;
}

// Mirrors `AiWorkflowFindingSchema` of the scanning agent; empty `sectionId` / `codeSnippet` are omitted so they do not constrain the match.
export interface IGoldenDatasetFinding {
  ruleId: string;
  sectionId?: string;
  elementSelector: string;
  codeSnippet?: string;
  actualResult: string;
  viewport?: IAuditFindingViewport;
  screenshotAltText?: string;
}

export type GoldenDatasetMatchRule = 'fuzzy' | 'contains' | 'exact' | 'judge' | 'excludes';

export interface IGoldenDatasetCase {
  case: string;
  value: IGoldenDatasetFinding;
  // overrides the suite-level rule for this case only; the export leaves it empty so every case inherits the suite rule
  rule?: GoldenDatasetMatchRule;
}

// Generic Eval import format: one suite per (golden evaluation, page), each finding is a case.
// `input` is the page url: the workflow runs once per suite and every case is scored against that single result.
// `rule` is the match rule shared by all cases of the suite; a case inherits it unless it has its own `rule`.
export interface IGoldenDatasetSuite {
  suite: string;
  input: string;
  rule: GoldenDatasetMatchRule;
  cases: IGoldenDatasetCase[];
}

export interface IManualAuditDaoUpdateRequest extends Omit<IManualAuditUpdateRequest, $manualAudit.status> {
  [$manualAudit.status]?: manualAuditStatus;
  [$manualAudit.aiTestingPagesTotal]?: number;
  [$manualAudit.aiTestingPageResults]?: IAiTestingPageResult[];
  [$manualAudit.aiTestingStartedAt]?: Date;
}

export enum $AMPImport {
  externalId = 'externalId',
  bestPracticeId = 'bestPracticeId',
  moduleName = 'moduleName',
  moduleURL = 'moduleURL',
  global = 'global',
  screenshot = 'screenshot',
  altText = 'altText',
  assistiveTechnologyCombination = 'assistiveTechnologyCombination',
  violationDescription = 'violationDescription',
  stepsToReproduce = 'stepsToReproduce',
  description = 'description',
  tractability = 'tractability',
  severity = 'severity',
  instanceDescription = 'instanceDescription',
  disabilitiesAffected = 'disabilitiesAffected',
  note = 'note',
  status = 'status',
  instanceId = 'instanceId',
  createdTimestamp = 'createdTimestamp',
}

interface IAltTextRequiresScreenshot {
  [$AMPImport.screenshot]: string;
  [$AMPImport.altText]: string;
}

interface IAltTextRequiresScreenshot2 {
  [$AMPImport.screenshot]: never;
  [$AMPImport.altText]: never;
}

interface IAltTextRequiresScreenshot3 {
  [$AMPImport.screenshot]: string;
  [$AMPImport.altText]: never;
}

interface IAMPImportBase {
  [$AMPImport.instanceId]?: string;
  [$AMPImport.bestPracticeId]: string;
  [$AMPImport.moduleName]: string;
  [$AMPImport.moduleURL]: string;
  [$AMPImport.global]: AuditFindingIssueFrequency;
  [$AMPImport.screenshot]?: string; // base64 monstrosity,
  [$AMPImport.altText]?: string;
  [$AMPImport.assistiveTechnologyCombination]?: string;
  [$AMPImport.violationDescription]?: string;
  [$AMPImport.stepsToReproduce]?: string;
  [$AMPImport.description]?: string;
  [$AMPImport.tractability]?: AuditFindingFunctionalImpact;
  [$AMPImport.severity]?: AuditFindingAccessibilityImpact;
  [$AMPImport.instanceDescription]?: string;
  [$AMPImport.disabilitiesAffected]?: AuditFindingDisabilityAffected[];
  [$AMPImport.note]?: string;
  [$AMPImport.status]?: AuditFindingQAStatus;
  [$AMPImport.createdTimestamp]?: number;
}

export type IAMPImport = IAMPImportBase &
  (IAltTextRequiresScreenshot | IAltTextRequiresScreenshot2 | IAltTextRequiresScreenshot3);
export type IAMPPublicImport = Omit<IAMPImportBase, $AMPImport.instanceId> & { [$AMPImport.externalId]?: string } & (
    IAltTextRequiresScreenshot | IAltTextRequiresScreenshot2 | IAltTextRequiresScreenshot3
  );

export interface IManualImport {
  [$auditFinding.externalId]?: string;
  [$auditFinding.actualResult]: string;
  [$auditFinding.elementReference]: string;
  [$auditFinding.ruleId]: string;
  [$auditFinding.issueId]: string;
  [$auditFinding.accessibilityImpact]: AuditFindingAccessibilityImpact;
  [$auditFinding.ATCombination]: string;
  [$auditFinding.disabilityAffected]: AuditFindingDisabilityAffected[];
  [$auditFinding.functionalImpact]: AuditFindingFunctionalImpact;
  [$auditFinding.instances]: string;
  [$auditFinding.issueFrequency]: AuditFindingIssueFrequency;
  [$auditFinding.QAComment]: string;
  [$auditFinding.QARecommendation]: string;
  [$auditFinding.QAScreencast]: string;
  [$auditFinding.QAStatus]: AuditFindingQAStatus;
  [$auditFinding.recommendation]: string;
  [$auditFinding.screen]: string;
  [$auditFinding.screenshot]: string[];
  [$auditFinding.stepsToReproduce]: string;
  [$auditFinding.summary]: string;
  [$auditFinding.attachment]: string[];
  [$auditFinding.createdAt]?: Date;
  url: string;
}

export interface IAMPImportErrors {
  // index starts at 1
  entry: number;
  attribute: string;
  error: Error;
}

export interface IManualEvalAiTestingRequest {
  scanId: string;
  pageId: string;
  url: string;
  sectionSelectors: Array<{ id: string; cssSelector: string }>;
  options: {
    secureWebAuthentication?: ISecureWebAuthentication;
    basicAuthentication?: { username: string; password: string };
    handlePopupsSelector?: string;
  };
  runCUET: boolean;
  standards: IParsedStandard[];
  customData?: Record<string, unknown>;
}

export interface IPageAiScanFinding {
  ruleId: string;
  sectionId: string;
  elementSelector: string;
  codeSnippet?: string;
  actualResult: string;
  confidence?: number;
  screenshotPath?: string;
  screenshotAltText?: string;
  findingType?: AuditFindingType;
  viewport?: IAuditFindingViewport;
}

export enum AiScanStatus {
  completed = 'completed',
  failed = 'failed',
}

export interface IManualEvalAiTestingResult {
  scanId: string;
  digitalPropertyId: string;
  pageId: string;
  url: string;
  findings?: IPageAiScanFinding[];
  findingsError?: string[];
  viewport?: unknown;
}

export const manualAuditToScanFieldMapping: Partial<Record<$manualAudit, $scan>> = {
  [$manualAudit.title]: $scan.title,
  [$manualAudit.status]: $scan.manualAuditStatus,
  [$manualAudit.aiTestingPagesTotal]: $scan.aiTestingPagesTotal,
  [$manualAudit.aiTestingPageResults]: $scan.aiTestingPageResults,
  [$manualAudit.aiTestingStartedAt]: $scan.aiTestingStartedAt,
};
